import client from "./client";
import { ROLES } from "../config/rbacConfig";
import { getCachedValue, setCachedValue } from "./cacheService";
import { markCacheSeeded } from "./cacheMeta";

const ROLES_CACHE_KEY = 'reference:roles';

function unwrapPayload(responseData) {
  const envelope = responseData ?? {};
  return envelope.data ?? envelope;
}

function normalizeRole(row = {}) {
  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    code: row.code ?? "",
    name: row.name ?? "",
    description: row.description ?? "",
    scope: row.scope ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
  };
}

function extractRoleList(responseData) {
  const payload = unwrapPayload(responseData);
  const items =
    Array.isArray(payload) ? payload :
    Array.isArray(payload.items) ? payload.items :
    Array.isArray(payload.results) ? payload.results :
    [];

  return items.map(normalizeRole);
}

function extractRoleRecord(responseData) {
  const payload = unwrapPayload(responseData);
  if (Array.isArray(payload)) return normalizeRole(payload[0] ?? {});
  if (payload?.item) return normalizeRole(payload.item);
  if (payload?.items && Array.isArray(payload.items)) {
    return normalizeRole(payload.items[0] ?? {});
  }
  return normalizeRole(payload);
}

function getStaticRoleFallback() {
  return Object.entries(ROLES).map(([roleId, role]) =>
    normalizeRole({
      uuid: roleId,
      id: roleId,
      code: role.backendClass,
      name: role.label,
      description: role.primaryFunction,
      scope: role.geographicScope,
    })
  );
}

export const rolesApi = {
  async list(params = {}) {
    try {
      const { data } = await client.get("/v1/roles", { params });
      const roles = extractRoleList(data);
      setCachedValue(ROLES_CACHE_KEY, roles, 24 * 60 * 60 * 1000);
      markCacheSeeded({ source: 'roles' });
      return roles;
    } catch (error) {
      const cached = getCachedValue(ROLES_CACHE_KEY)?.value;
      if (Array.isArray(cached) && cached.length > 0) return cached;

      const fallback = getStaticRoleFallback();
      if (fallback.length > 0) {
        setCachedValue(ROLES_CACHE_KEY, fallback, 24 * 60 * 60 * 1000);
        markCacheSeeded({ source: 'roles:fallback' });
        return fallback;
      }

      throw error;
    }
  },

  async get(uuid) {
    try {
      const { data } = await client.get(`/v1/roles/${uuid}`);
      const role = extractRoleRecord(data);
      if (role?.uuid || role?.id) {
        const cached = getCachedValue(ROLES_CACHE_KEY)?.value;
        const next = Array.isArray(cached) ? cached : [];
        const index = next.findIndex((item) => String(item?.uuid ?? item?.id ?? '') === String(role.uuid ?? role.id ?? ''));
        if (index >= 0) {
          next[index] = role;
        } else {
          next.push(role);
        }
        setCachedValue(ROLES_CACHE_KEY, next, 24 * 60 * 60 * 1000);
      }
      return role;
    } catch (error) {
      const cached = getCachedValue(ROLES_CACHE_KEY)?.value;
      if (Array.isArray(cached) && cached.length > 0) {
        const match = cached.find((item) => String(item?.uuid ?? item?.id ?? '') === String(uuid ?? ''));
        if (match) return match;
      }

      const fallback = getStaticRoleFallback();
      const match = fallback.find((item) => {
        const keys = [item.uuid, item.id, item.code];
        return keys.some((key) => String(key ?? '') === String(uuid ?? ''));
      });
      if (match) return match;

      throw error;
    }
  },
};

export { normalizeRole };
